import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';

import {
  useCustomerOrderDocuments,
  useDeleteCustomerOrderDocument,
  useDownloadCustomerOrderDocument,
  usePreviewCustomerOrderDocument,
  useUploadCustomerOrderDocument,
} from '../../features/customer-orders/api';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog/PdfPreviewDialog';
import './CustomerOrderDocuments.preview.scss';

type Props = {
  orderId: string;
};

type PdfPreviewState = {
  url: string;
  documentId: string;
  fileName: string;
};

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
];

const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',');

function formatFileSize(size?: number | null) {
  if (!size) return '—';

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(fileName: string) {
  const lastDotIndex = fileName.lastIndexOf('.');

  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
}

function isPreviewablePdf(document: { fileName: string }): boolean {
  if (getFileExtension(document.fileName) === '.pdf') return true;

  // MIME type is optional in customer order-document API responses.
  return (
    'mimeType' in document &&
    typeof document.mimeType === 'string' &&
    document.mimeType.toLowerCase().split(';')[0].trim() === 'application/pdf'
  );
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'The document must be 15 MB or smaller.';
  }

  if (!ACCEPTED_EXTENSIONS.includes(getFileExtension(file.name))) {
    return 'This document type is not supported.';
  }

  return null;
}

export function CustomerOrderDocuments({ orderId }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRequestRef = useRef(0);

  const documentsQuery = useCustomerOrderDocuments(orderId);
  const uploadDocument = useUploadCustomerOrderDocument(orderId);
  const deleteDocument = useDeleteCustomerOrderDocument(orderId);
  const downloadDocument = useDownloadCustomerOrderDocument(orderId);
  const previewDocument = usePreviewCustomerOrderDocument(orderId);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PdfPreviewState | null>(null);

  // Close a previous order's document and disregard pending responses after
  // switching orders or unmounting. PdfPreviewDialog revokes the object URL.
  useEffect(() => {
    setPreview(null);
    setPreviewingId(null);
    return () => {
      previewRequestRef.current += 1;
    };
  }, [orderId]);

  const documents = documentsQuery.data?.documents ?? [];

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      await uploadDocument.mutateAsync(file);
      toast.success('Document uploaded.');
    } catch (error: any) {
      const code = error?.response?.data?.error;

      if (code === 'FILE_TOO_LARGE') {
        toast.error('The document must be 15 MB or smaller.');
      } else if (code === 'UNSUPPORTED_FILE_TYPE') {
        toast.error('This document type is not supported.');
      } else {
        toast.error(code || 'Failed to upload document.');
      }
    }
  };

  const handleDelete = async (documentId: string, fileName: string) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;

    try {
      await deleteDocument.mutateAsync(documentId);
      toast.success('Document deleted.');
    } catch (error: any) {
      const code = error?.response?.data?.error;

      if (code === 'DOCUMENT_DELETE_FORBIDDEN') {
        toast.error('You can only delete documents that you uploaded.');
      } else {
        toast.error(code || 'Failed to delete document.');
      }
    }
  };

  const handleDownload = async (documentId: string, fileName: string) => {
    try {
      setDownloadingId(documentId);
      await downloadDocument.mutateAsync({ documentId, fileName });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to download document.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = async (documentId: string, fileName: string) => {
    const requestId = ++previewRequestRef.current;
    setPreviewingId(documentId);

    try {
      const blob = await previewDocument.mutateAsync(documentId);
      if (requestId !== previewRequestRef.current) return;

      const url = URL.createObjectURL(blob);
      setPreview({ url, documentId, fileName });
    } catch (error: any) {
      if (requestId === previewRequestRef.current) {
        toast.error(error?.response?.data?.error || 'Failed to preview PDF.');
      }
    } finally {
      if (requestId === previewRequestRef.current) {
        setPreviewingId(null);
      }
    }
  };

  return (
    <section className="customer-card customer-order-documents">
      <div className="customer-order-documents__header">
        <div>
          <h4 className="customer-card__title" style={{ marginBottom: 4 }}>
            Documents
          </h4>
          <div className="muted tiny">PDF, Word, Excel, CSV, text, or image files up to 15 MB.</div>
        </div>

        <input
          ref={inputRef}
          type="file"
          hidden
          accept={ACCEPT_ATTRIBUTE}
          onChange={handleFileChange}
        />

        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploadDocument.isPending}
        >
          <Upload size={14} />
          {uploadDocument.isPending ? 'Uploading…' : 'Upload document'}
        </button>
      </div>

      {documentsQuery.isLoading ? (
        <div className="skeleton" style={{ height: 80 }} />
      ) : documents.length === 0 ? (
        <div className="muted">No documents are available for this order.</div>
      ) : (
        <div className="customer-order-documents__list">
          {documents.map((document) => {
            const isDownloading = downloadingId === document.id;
            const isPdf = isPreviewablePdf(document);

            return (
              <div key={document.id} className="customer-order-documents__row">
                <FileText size={18} className="customer-order-documents__icon" />

                <div className="customer-order-documents__details">
                  <div className="customer-order-documents__name" title={document.fileName}>
                    {document.fileName}
                  </div>

                  <div className="muted tiny">
                    {formatFileSize(document.sizeBytes)}
                    {' · '}
                    {new Date(document.uploadedAt).toLocaleString()}
                    {' · '}
                    {document.canDelete ? 'Uploaded by you' : 'Shared document'}
                  </div>
                </div>

                <div className="customer-order-documents__actions">
                  {isPdf && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm customer-order-documents__preview-btn"
                      onClick={() => handlePreview(document.id, document.fileName)}
                      disabled={previewingId !== null}
                      aria-label={`Preview ${document.fileName}`}
                    >
                      <Eye size={14} />
                      {previewingId === document.id ? 'Opening…' : 'Preview'}
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => handleDownload(document.id, document.fileName)}
                    disabled={isDownloading}
                    aria-label={`Download ${document.fileName}`}
                  >
                    <Download size={14} />
                    {isDownloading ? 'Downloading…' : 'Download'}
                  </button>

                  {document.canDelete && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => handleDelete(document.id, document.fileName)}
                      disabled={deleteDocument.isPending}
                      aria-label={`Delete ${document.fileName}`}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <PdfPreviewDialog
          url={preview.url}
          title={`PDF Preview · ${preview.fileName}`}
          onClose={() => setPreview(null)}
          footer={
            <div className="actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => handleDownload(preview.documentId, preview.fileName)}
                disabled={downloadingId === preview.documentId}
              >
                <Download size={14} />
                {downloadingId === preview.documentId ? 'Downloading…' : 'Download PDF'}
              </button>
            </div>
          }
        />
      )}
    </section>
  );
}
